import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { signOut } from 'firebase/auth';
import { Sun, Moon, HelpCircle, Sparkles, LogOut, ChevronsUpDown, User } from 'lucide-react';
import { auth } from '../firebase';
import { Avatar } from './Avatar';
import { useThemeStore } from '../store/themeStore';
import { useOnboardingStore } from '../store/onboardingStore';
import type { EntityUser } from '../types';

interface AvatarMenuProps {
  user: Pick<EntityUser, 'fullName' | 'profilePictureUrl'> | null | undefined;
  name: string;
  subtitle: string;
  collapsed: boolean;
  /** Called when a navigating action fires — used to close the mobile drawer. */
  onNavigate?: () => void;
}

const MENU_W = 232;

function MenuItem({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-meta focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-inset transition-colors ${
        danger
          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-900 dark:hover:text-white'
      }`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      {label}
    </button>
  );
}

export function AvatarMenu({ user, name, subtitle, collapsed, onNavigate }: AvatarMenuProps) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useThemeStore();
  const requestReplay = useOnboardingStore((s) => s.requestReplay);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const openMenu = useCallback(() => {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  const handleLogout = async () => {
    close();
    onNavigate?.();
    await signOut(auth);
    navigate('/login');
  };

  const handleSupport = () => {
    close();
    onNavigate?.();
    navigate('/support');
  };

  // The avatar menu is the most reflexive place to look for personal
  // settings — link it to the same tab the Settings page calls "My
  // Profile", with the same User icon, so the two surfaces read as one.
  const handleMySettings = () => {
    close();
    onNavigate?.();
    navigate('/settings?tab=my');
  };

  // Re-trigger onboarding: request a replay and land on the home dashboard,
  // which consumes the flag and re-opens the Welcome card.
  const handleReplayTour = () => {
    close();
    onNavigate?.();
    requestReplay();
    navigate('/');
  };

  // Flyout opens upward, anchored to the trigger's top-left, clamped to viewport.
  const left = rect ? Math.max(8, Math.min(rect.left, window.innerWidth - MENU_W - 8)) : 8;
  const bottom = rect ? window.innerHeight - rect.top + 8 : 8;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={openMenu}
        title="Account"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center gap-2 w-full px-3 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-primary/50 transition-all ${
          collapsed ? 'justify-center' : ''
        } ${open ? 'bg-gray-100 dark:bg-white/[0.06]' : ''}`}
      >
        <Avatar user={user} size="sm" />
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-meta text-gray-900 dark:text-white font-medium truncate">{name}</p>
              <p className="text-micro text-gray-500 truncate capitalize">{subtitle}</p>
            </div>
            <ChevronsUpDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </>
        )}
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              {/* Invisible backdrop — catches outside clicks */}
              <div className="fixed inset-0 z-40" onClick={close} />
              <motion.div
                initial={{ opacity: 0, y: 6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.98 }}
                transition={{ duration: 0.14, ease: 'easeOut' }}
                style={{ position: 'fixed', left, bottom, width: MENU_W, zIndex: 9999 }}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/[0.12] rounded-2xl shadow-2xl overflow-hidden py-1.5"
              >
                {/* Header */}
                <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-gray-100 dark:border-white/[0.06] mb-1">
                  <Avatar user={user} size="md" />
                  <div className="min-w-0">
                    <p className="text-meta font-semibold text-gray-900 dark:text-white truncate">{name}</p>
                    <p className="text-micro text-gray-500 dark:text-gray-400 truncate capitalize">{subtitle}</p>
                  </div>
                </div>

                {/* Theme toggle stays open so the user sees the label flip */}
                <MenuItem
                  icon={theme === 'dark' ? Sun : Moon}
                  label={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                  onClick={toggleTheme}
                />
                <MenuItem icon={User} label="My Profile" onClick={handleMySettings} />
                <MenuItem icon={HelpCircle} label="Support" onClick={handleSupport} />
                <MenuItem icon={Sparkles} label="Restart Guided Tour" onClick={handleReplayTour} />

                <div className="my-1 border-t border-gray-100 dark:border-white/[0.06]" />

                <MenuItem icon={LogOut} label="Log out" onClick={handleLogout} danger />
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
